<template>
	<Dialog
		:dialog-visible="dialog"
		title="Edit User"
		:agree-text="saving ? 'Saving' : 'Save'"
		cancel-text="Cancel"
		:disable-agree-button="saving || loading || !isDirty"
		:agree-loading="saving"
		:options="{ width: '500px' }"
		@cancel="cancel"
		@agree="agree">
		<template #dialog-body>
			<div v-if="loading" class="d-flex justify-center pa-4">
				<v-progress-circular indeterminate color="primary" />
			</div>
			<v-form v-else ref="formRef" v-model="isFormValid">
				<CharacterLimitedTextField
					v-model.trim="form.firstname"
					field-noun="first name"
					:rules="firstnameRules"
					:max-length="FIELD_LENGTHS.NAME"
					autofocus>
					<template #label><FieldLabel label="First name (Required)" /></template>
				</CharacterLimitedTextField>
				<CharacterLimitedTextField
					v-model.trim="form.lastname"
					field-noun="last name"
					:rules="lastnameRules"
					:max-length="FIELD_LENGTHS.NAME">
					<template #label><FieldLabel label="Last name (Required)" /></template>
				</CharacterLimitedTextField>
				<v-text-field
					:model-value="displayEmail"
					label="Email address"
					disabled
					hint="Email address cannot be edited."
					persistent-hint />
				<div @paste.prevent="pasteEvent">
					<vue-tel-input
						v-bind="vueTelProps"
						v-model="form.phone"
						style="border-radius: 6px; padding: 9px"
						valid-color="#000000"
						:class="{ 'error--text': showPhoneFieldErrorState }"
						@validate="validatePhone"
						@blur="phoneBlur"
						@focus="phoneFocus" />
				</div>
				<div class="d-flex align-center ga-3 mt-1">
					<span class="text-body-2">Avatar Color</span>
					<ColorPicker v-model="form.avatarColor" source="user" />
				</div>
			</v-form>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
	import { ref, computed } from "vue";
	import Dialog from "@/components/Dialog.vue";
	import ColorPicker from "@/components/tags/ColorPicker.vue";
	import api from "@/api";
	import type { User } from "@/types/User";
	import { FIELD_LENGTHS, validateFormOrWarn } from "@/helpers/Validation";
	import { getVueTelProps, getVueTelInputOptions } from "@/helpers/phone";
	import { getTrimmedPhoneFromClipboard, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { useToast } from "vue-toastification";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const toast = useToast();

	const dialog = ref(false);
	const loading = ref(false);
	const saving = ref(false);
	const isFormValid = ref(false);
	const formRef = ref<any>(null);
	const resolve = ref<((value: User | null) => void) | null>(null);
	const displayEmail = ref("");
	const userId = ref<string | null>(null);
	const validPhone = ref(true);
	const showPhoneFieldErrorState = ref(false);

	const form = ref({
		firstname: "",
		lastname: "",
		phone: "",
		avatarColor: ""
	});

	const originalForm = ref({
		firstname: "",
		lastname: "",
		phone: "",
		avatarColor: ""
	});

	const isDirty = computed(
		() =>
			form.value.firstname.trim() !== originalForm.value.firstname ||
			form.value.lastname.trim() !== originalForm.value.lastname ||
			(form.value.phone || "") !== (originalForm.value.phone || "") ||
			form.value.avatarColor !== originalForm.value.avatarColor
	);

	const vueTelProps = getVueTelProps({
		inputOptions: getVueTelInputOptions({
			placeholder: "Phone Number (Optional)",
			autofocus: false
		})
	});

	// FIELD_LENGTHS.NAME (45 code points) is now enforced by CharacterLimitedTextField's :max-length
	// (LSP-16852). No server-unit backstop needed: this dialog edits USERS, not customers —
	// apps/contact-api/src/controllers/users.ts has no length check on firstname/lastname (only a
	// presence/`.trim()` guard), and the User model's columns are bare `DataTypes.STRING`
	// (Sequelize's default varchar(255)). 45 code points is at most 90 UTF-16 units — under that
	// column width. If a narrower validator is ever added to users.ts, or this cap is raised above
	// 127, add VuetifyValidation.ServerUtf16BackstopValidation (src/helpers/Validation.ts) as a rule here.
	const firstnameRules = [(v: string) => (v || "").trim().length > 0 || "First name is required."];

	const lastnameRules = [(v: string) => (v || "").trim().length > 0 || "Last name is required."];

	function validatePhone(args: any) {
		if (!form.value.phone) {
			validPhone.value = true;
			return;
		}
		validPhone.value = args?.valid;
		if (validPhone.value && args?.formatted && form.value.phone !== args.formatted) {
			form.value.phone = args.formatted;
		}
	}

	function phoneBlur() {
		showPhoneFieldErrorState.value = !validPhone.value;
	}

	function phoneFocus() {
		showPhoneFieldErrorState.value = false;
	}

	function pasteEvent(event: ClipboardEvent) {
		form.value.phone = getTrimmedPhoneFromClipboard(event);
	}

	async function open(id: string): Promise<User | null> {
		userId.value = id;
		displayEmail.value = "";
		form.value = { firstname: "", lastname: "", phone: "", avatarColor: "" };
		// LSP-16852/16859 (PR #3271 review — Finding 2, MEDIUM): reset alongside form.value, not only
		// after api.getUser() resolves. UserAutoComplete.vue mounts ONE persistent EditUserDialog and
		// calls .open(userId) per edit click, so without this reset a second open() left originalForm
		// holding the PREVIOUS user's data while form.value was already cleared — isDirty read true
		// during the load, rendering Save enabled over the loading spinner (see the disable-agree-button
		// binding above, which also now covers `loading` as a belt-and-suspenders guard).
		originalForm.value = { firstname: "", lastname: "", phone: "", avatarColor: "" };
		validPhone.value = true;
		showPhoneFieldErrorState.value = false;
		saving.value = false;
		dialog.value = true;
		loading.value = true;

		try {
			const user = await api.getUser(id);
			const loaded = {
				firstname: user.firstname || "",
				lastname: user.lastname || "",
				phone: user.phone || "",
				avatarColor: user.Account?.avatarColor || ""
			};
			form.value = { ...loaded };
			originalForm.value = { ...loaded };
			displayEmail.value = user.email || "";
		} catch (e) {
			const message = getMessageFromError(e) || "Something went wrong loading the user. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			dialog.value = false;
			return null;
		} finally {
			loading.value = false;
		}

		return new Promise((res) => {
			resolve.value = res;
		});
	}

	async function agree() {
		if (!validPhone.value && form.value.phone) {
			showPhoneFieldErrorState.value = true;
			return;
		}
		// LSP-16852/16859 (PR #3271 review — Finding 2): validateFormOrWarn distinguishes "form ref not
		// mounted" (its own warning, fired when a stray click lands while the `v-if="loading"` spinner
		// is showing and `formRef` — bound to the `v-else` form — is still null) from "rules genuinely
		// failed" (the branch below). The two used to be conflated under one raw `formRef.value?.validate()`
		// call, whose `undefined` result during the loading race misattributed a not-yet-mounted-form
		// click as a real validation failure.
		const validationResult = await validateFormOrWarn(formRef.value, "[EditUserDialog] agree: form ref not mounted, cannot validate");
		if (!validationResult) {
			return;
		}
		if (!validationResult.valid) {
			// Save no longer disables on invalid validity (root CLAUDE.md — "Form action buttons —
			// always enabled, no-op when invalid"), so a click while a name field is empty or over its
			// code-point limit now reaches here and silently no-ops. Log it per the silent-failure
			// diagnostic rule so a future "Save did nothing" report is triageable from logs alone.
			console.warn("EditUserDialog.agree: blocked — form failed validation, not saving", { userId: userId.value });
			return;
		}

		try {
			saving.value = true;
			// Explicitly send only editable fields — never include email
			const updated: User = await api.updateUser(userId.value!, {
				firstname: form.value.firstname.trim(),
				lastname: form.value.lastname.trim(),
				phone: form.value.phone || null,
				Account: { avatarColor: form.value.avatarColor }
			});
			toast.success("User updated successfully.");
			resolve.value?.(updated);
			dialog.value = false;
		} catch (e) {
			const message = getMessageFromError(e) || "Something went wrong updating the user. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			saving.value = false;
		}
	}

	function cancel() {
		dialog.value = false;
		resolve.value?.(null);
	}

	defineExpose({ open });
</script>
